import { defineStore, DefineStoreOptions } from 'pinia';
import { AdvancedSearchConfiguration, FunctionalityContext, Header, Pagination, Payment, PaymentStatus } from '@/types';
import PaymentFormatter from '@/services/Payment/Formatter/PaymentFormatter';
import { PaginatedListHeader } from '@/services/Factory/PaginatedListHeadersFactory';
import paymentService from '@/services/api/payment/payment';
import advancedSearchService from '@/services/advancedSearch';
import { ScopeEnum } from '@/types/enum/ScopeEnum';
import { PaymentTypeEnum } from '@/types/enum/PaymentTypeEnum';

interface PaymentsState {
	payments: Payment[];
	context: FunctionalityContext;
	totalCount: number;
	nonSupportPaymentsCount: number;
	headers: Array<PaginatedListHeader>;
	tableLoading: boolean;
	search: string;
	filters: string[] | null;
	filtersConfiguration: any;
	advancedSearchConfiguration: AdvancedSearchConfiguration;
	advancedSearch: any;
	dataTableOptions: any;
	filterBuffer: any;
}

const paymentStores: any = {

	getStoreByScopeAndType(scope: string, type: string) {
		let store: any;
		const areExpPayments = PaymentTypeEnum.PAYMENT_EXP === type;

		switch (scope) {
			case ScopeEnum.VALIDATION:
				store = areExpPayments ? usePaymentExpValidationStore() : usePaymentPcrValidationStore();
				break;
			case ScopeEnum.VALIDATION_DGOS:
				store = usePaymentDgosValidationStore();
				break;
			case ScopeEnum.LIQUIDATION:
				store = areExpPayments ? usePaymentExpLiquidationStore() : usePaymentPcrLiquidationStore();
				break;
			case ScopeEnum.SUPERVISION:
				store = areExpPayments ? usePaymentExpSupervisionStore() : usePaymentPcrSupervisionStore();
				break;
			case ScopeEnum.ADMINISTRATION:
				store = areExpPayments ? usePaymentExpAdministrationStore() : usePaymentPcrAdministrationStore();
				break;
			case ScopeEnum.EVALUATION_TC:
				store = usePaymentEvaluationTcStore();
				break;
			case ScopeEnum.EVALUATION_DM:
				store = usePaymentEvaluationDmStore();
				break;
		}

		return store;
	},

	getDefaultStore() {
		return usePaymentExpStore();
	},

	reset(): void {
		usePaymentExpStore().$reset();
		usePaymentPcrStore().$reset();
		usePaymentExpValidationStore().$reset();
		usePaymentPcrValidationStore().$reset();
		usePaymentDgosValidationStore().$reset();
		usePaymentExpLiquidationStore().$reset();
		usePaymentPcrLiquidationStore().$reset();
		usePaymentExpSupervisionStore().$reset();
		usePaymentPcrSupervisionStore().$reset();
		usePaymentExpAdministrationStore().$reset();
		usePaymentPcrAdministrationStore().$reset();
		usePaymentEvaluationTcStore().$reset();
		usePaymentEvaluationDmStore().$reset();
	}
};
// EXP
const usePaymentExpStore = defineStore('payment-exp', getOptions());
// Stores AccessMaster
const usePaymentExpValidationStore = defineStore('payment-validation-exp', getOptions());
const usePaymentExpLiquidationStore = defineStore('payment-liquidation-exp', getOptions());
const usePaymentExpSupervisionStore = defineStore('payment-supervision-exp', getOptions());
const usePaymentExpAdministrationStore = defineStore('payment-administration-exp', getOptions());

//PCR
const usePaymentPcrStore = defineStore('payment-pcr', getOptions());
// Stores AccessMaster
const usePaymentPcrValidationStore = defineStore('payment-validation-pcr', getOptions());
const usePaymentPcrLiquidationStore = defineStore('payment-liquidation-pcr', getOptions());
const usePaymentPcrSupervisionStore = defineStore('payment-supervision-pcr', getOptions());
const usePaymentPcrAdministrationStore = defineStore('payment-administration-pcr', getOptions());
const usePaymentDgosValidationStore = defineStore('payment-validation-dgos', getOptions());

const usePaymentEvaluationTcStore = defineStore('payment-evaluation-tc', getOptions());
const usePaymentEvaluationDmStore = defineStore('payment-evaluation-dm', getOptions());

function getOptions() {
	return {
		id: 'PaymentStore',
		state: (): PaymentsState => ({
			payments: [],
			context: {
				translations: {
					labelFiltersSelector: '',
					labelAllFilters: ''
				},
				options: {
					displayExportBtn: false,
					filters: [],
					canApproveWithValidation: false,
					canApproveAllAsync: false
				}
			},
			totalCount: 0,
			nonSupportPaymentsCount: 0,
			headers: [],
			tableLoading: false,
			search: '',
			filters: [],
			filtersConfiguration: {},
			advancedSearchConfiguration: {
				filters: [],
				values: []
			},
			advancedSearch: {},
			dataTableOptions: {},
			filterBuffer: {}
		}),
		persist: {
			storage: localStorage,
			paths: ['payments', 'context', 'totalCount', 'headers', 'search', 'completion', 'filters', 'filtersConfiguration', 'dataTableOptions']
		},
		actions: {
			async fetch(pagination: Pagination, search: string, projectToken?: string | null, type?: string | null, scope?: string | null, advancedSearch?: any): Promise<void> {
				const response = await paymentService.fetchAllPayments(pagination, search, projectToken, type, scope, advancedSearch);
				this.headers = response.data.headers;
				this.payments = response.data.items;

				const statusHeader = this.headers.find((item: Header)  => 'status' === item.code);
				const statusList: Array<PaymentStatus> = statusHeader?.options?.choices ?? [];

				this.payments.forEach((payment: Payment) => {
					PaymentFormatter.formatStatus(payment, statusList);
				});

				this.context = response.data.context;
				this.totalCount = response.data.totalCount;
			},

			async fetchPaymentsCount(scope: string, type: string): Promise<void> {
				if ('validation' !== scope) {
					return;
				}

				const response = await paymentService.fetchNonSupportPaymentsCount(scope, type);
				this.nonSupportPaymentsCount = response.data.paymentsCount;
			},

			async updateFilters(filters: any): Promise<void> {
				filters.forEach((filter: any) => {
					this.advancedSearch[filter.name] = filter.value;
				});
			},

			async setBuffer(filters: any): Promise<void> {
				this.filterBuffer = filters;
			},

			async loadAdvancedSearch(projectToken?: string | null, scope: string | null = null, type: string | null = null): Promise<void> {
				const response = await paymentService.getPaymentsAdvancedSearch(projectToken, scope, type);

				this.advancedSearchConfiguration = response.data.advancedSearch;
				if (this.filterBuffer !== this.advancedSearchConfiguration.filters && this.filterBuffer.length > 0) {
					this.advancedSearchConfiguration.filters = this.filterBuffer;
				}

				advancedSearchService.formatChip(this.advancedSearchConfiguration);
			},

			getFiltersCodes(filters: string[]): string[] {
				const filtersCodes: string[] | any = [];

				filters.forEach((filter: string) => {
					if (Object.keys(this.filtersConfiguration).includes(filter)) {
						filtersCodes.push(filter);
					}

					if (Object.values(this.filtersConfiguration).includes(filter)) {
						filtersCodes.push(Object.keys(this.filtersConfiguration).find(key => this.filtersConfiguration[key] === filter));
					}
				});

				return filtersCodes;
			},

			getFiltersLabel(filters: string[]): string[] {
				const filtersLabel: string[] | any = [];

				filters.forEach((filter: string) => {
					if (Object.values(this.filtersConfiguration).includes(filter)) {
						filtersLabel.push(filter);
					}

					if (Object.keys(this.filtersConfiguration).includes(filter)) {
						filtersLabel.push(this.filtersConfiguration[filter]);
					}
				});

				return filtersLabel;
			},

			formatFilters(defaultFilters: string[] | null, scope: string): string[] {
				if (0 === this.filters.length && ScopeEnum.LIQUIDATION === scope && null !== defaultFilters) {
					return this.getFiltersCodes(defaultFilters);
				}

				return this.getFiltersCodes(this.filters);
			},

			displayApproveAll(nonSupportPaymentsCount: number, canApproveAllAsync = false) {
				return canApproveAllAsync && nonSupportPaymentsCount !== 0;
			}
		},
		getters: {
			filtersCodeLabel(state: PaymentsState) {
				return state.context.options.filters;
			},
			allFilters(state: PaymentsState) {
				return Object.values(state.context.options.filters);
			},
			labelFilters(state: PaymentsState) {
				return state.context.translations.labelFiltersSelector;
			},
			labelAllFilters(state: PaymentsState) {
				return state.context.translations.labelAllFilters;
			},
			displayBtnExportAll(state: PaymentsState) {
				return state.payments.length !== 0;
			}
		}
	} as DefineStoreOptions<string, PaymentsState, any, any>;
}

export default paymentStores;
